'use client';

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronRight, ChevronDown, Search, Users, User, UserCheck, UsersRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Realistic name pools
const firstNames = [
  // Indian Male Names
  'Aarav', 'Vivaan', 'Aditya', 'Vihaan', 'Arjun', 'Sai', 'Reyansh', 'Ayaan',
  'Krishna', 'Ishaan', 'Shaurya', 'Atharv', 'Advik', 'Pranav', 'Advait',
  'Aarush', 'Kabir', 'Ritvik', 'Anirudh', 'Dhruv', 'Arnav', 'Rudra', 'Yash',
  'Rohan', 'Karan', 'Vikram', 'Rahul', 'Nikhil', 'Siddharth', 'Varun',
  'Aakash', 'Abhishek', 'Ajay', 'Akshay', 'Amit', 'Ankit', 'Ashwin', 'Deepak',
  'Gaurav', 'Harish', 'Jayesh', 'Kunal', 'Manish', 'Mayank', 'Mohit', 'Naveen',
  'Pankaj', 'Parth', 'Prasad', 'Rajat', 'Ravi', 'Sachin', 'Sahil', 'Sandeep',
  'Suresh', 'Tarun', 'Tushar', 'Ujjwal', 'Venkat', 'Vikas', 'Vinay', 'Vishal',
  // Indian Female Names
  'Neha', 'Priya', 'Ananya', 'Diya', 'Aanya', 'Saanvi', 'Anika', 'Myra',
  'Sara', 'Ira', 'Navya', 'Aadya', 'Kiara', 'Avni', 'Anvi', 'Pihu',
  'Shreya', 'Kavya', 'Ishita', 'Tanya', 'Riya', 'Pooja', 'Sneha', 'Divya',
  'Anjali', 'Swati', 'Megha', 'Nisha', 'Pallavi', 'Rashmi', 'Simran', 'Sonali',
  'Sonia', 'Sunita', 'Tanvi', 'Trisha', 'Varsha', 'Vidya', 'Yamini', 'Zara',
  // International Names
  'James', 'Emily', 'Michael', 'Sarah', 'David', 'Jessica', 'Daniel', 'Ashley',
  'Matthew', 'Jennifer', 'Christopher', 'Amanda', 'Andrew', 'Stephanie', 'Joshua',
  'Nicole', 'Ryan', 'Elizabeth', 'Brandon', 'Megan', 'Tyler', 'Lauren', 'Kevin',
  'Rachel', 'Jason', 'Samantha', 'Justin', 'Katherine', 'Brian', 'Michelle',
  'William', 'Emma', 'Alexander', 'Olivia', 'Benjamin', 'Sophia', 'Lucas', 'Ava',
  'Henry', 'Isabella', 'Sebastian', 'Mia', 'Jack', 'Charlotte', 'Ethan', 'Amelia'
];

const lastNames = [
  // Indian Last Names
  'Sharma', 'Verma', 'Gupta', 'Singh', 'Kumar', 'Joshi', 'Patel', 'Shah',
  'Mehta', 'Reddy', 'Nair', 'Iyer', 'Menon', 'Rao', 'Pillai', 'Choudhary',
  'Agarwal', 'Bansal', 'Bhatt', 'Chauhan', 'Desai', 'Dubey', 'Goel', 'Goyal',
  'Jain', 'Kapoor', 'Kaur', 'Khan', 'Khanna', 'Kulkarni', 'Malhotra', 'Mishra',
  'Mukherjee', 'Nayak', 'Pandey', 'Patil', 'Prasad', 'Rajput', 'Rathore', 'Saxena',
  'Sehgal', 'Sengupta', 'Sethi', 'Sinha', 'Srivastava', 'Tiwari', 'Trivedi', 'Varma',
  // International Last Names
  'Johnson', 'Williams', 'Brown', 'Davis', 'Miller', 'Wilson', 'Anderson', 'Taylor',
  'Thomas', 'Moore', 'Jackson', 'Martin', 'Lee', 'Thompson', 'White', 'Harris',
  'Clark', 'Lewis', 'Robinson', 'Walker', 'Young', 'Allen', 'King', 'Wright',
  'Scott', 'Green', 'Baker', 'Adams', 'Nelson', 'Hill', 'Campbell', 'Mitchell'
];

// Seeded random for consistent names
function seededRandom(seed: number): () => number {
  return function() {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
}

// Hierarchy node interface
export interface HierarchyNode {
  id: string;
  name: string;
  email: string;
  avatar: string;
  department: string;
  designation: string;
  isManager: boolean;
  directReportsCount: number;
  children?: HierarchyNode[];
}

// Generate mock hierarchy data for 1000+ users
function generateMockHierarchy(): HierarchyNode {
  const departments = ['Engineering', 'Product', 'Design', 'Sales', 'Marketing', 'Operations', 'HR', 'Finance'];
  const designations = {
    level1: ['CEO'],
    level2: ['CTO', 'CFO', 'COO', 'VP Engineering', 'VP Product', 'VP Sales'],
    level3: ['Engineering Manager', 'Product Manager', 'Design Lead', 'Sales Director', 'Marketing Director'],
    level4: ['Senior Engineer', 'Tech Lead', 'Senior Designer', 'Account Manager', 'Marketing Manager'],
    level5: ['Software Engineer', 'Designer', 'Sales Executive', 'Marketing Executive', 'Analyst'],
  };

  let userId = 1;
  const random = seededRandom(42); // Consistent seed for reproducible names
  const usedNames = new Set<string>();

  const getRandomName = (): string => {
    let attempts = 0;
    while (attempts < 100) {
      const firstName = firstNames[Math.floor(random() * firstNames.length)];
      const lastName = lastNames[Math.floor(random() * lastNames.length)];
      const fullName = `${firstName} ${lastName}`;
      if (!usedNames.has(fullName)) {
        usedNames.add(fullName);
        return fullName;
      }
      attempts++;
    }
    // Fallback with unique suffix
    const firstName = firstNames[Math.floor(random() * firstNames.length)];
    const lastName = lastNames[Math.floor(random() * lastNames.length)];
    return `${firstName} ${lastName} ${userId}`;
  };

  const createNode = (name: string, designation: string, department: string, isManager: boolean): HierarchyNode => ({
    id: `user-${userId++}`,
    name,
    email: `${name.toLowerCase().replace(/\s+/g, '.')}@company.com`,
    // Empty — UI renders initial via AvatarFallback. Real reportees serve
    // their uploaded avatar through the BE; mock hierarchy never reaches a
    // live employee (gated by previewMode in manager-dashboard-content).
    avatar: '',
    department,
    designation,
    isManager,
    directReportsCount: 0,
    children: [],
  });

  // Create CEO
  const ceo = createNode('Rishi Nigam', 'CEO', 'Executive', true);

  // Create C-level executives (6) - use specific names for leadership
  const cLevel = [
    createNode('Ankush Johar', 'CTO', 'Engineering', true),
    createNode('Priya Mehta', 'CFO', 'Finance', true),
    createNode('Rajesh Kumar', 'COO', 'Operations', true),
    createNode('Sarah Johnson', 'VP Engineering', 'Engineering', true),
    createNode('Michael Chen', 'VP Product', 'Product', true),
    createNode('Amit Sharma', 'VP Sales', 'Sales', true),
  ];

  // For each C-level, create managers (5-8 each)
  cLevel.forEach((exec) => {
    const numManagers = 5 + Math.floor(random() * 4);
    for (let i = 0; i < numManagers; i++) {
      const dept = exec.department === 'Executive' ? departments[Math.floor(random() * departments.length)] : exec.department;
      const manager = createNode(
        getRandomName(),
        designations.level3[Math.floor(random() * designations.level3.length)],
        dept,
        true
      );

      // For each manager, create team leads (3-6 each)
      const numLeads = 3 + Math.floor(random() * 4);
      for (let j = 0; j < numLeads; j++) {
        const lead = createNode(
          getRandomName(),
          designations.level4[Math.floor(random() * designations.level4.length)],
          dept,
          true
        );

        // For each lead, create individual contributors (5-15 each)
        const numICs = 5 + Math.floor(random() * 11);
        for (let k = 0; k < numICs; k++) {
          const ic = createNode(
            getRandomName(),
            designations.level5[Math.floor(random() * designations.level5.length)],
            dept,
            false
          );
          lead.children!.push(ic);
        }
        lead.directReportsCount = lead.children!.length;
        manager.children!.push(lead);
      }
      manager.directReportsCount = manager.children!.length;
      exec.children!.push(manager);
    }
    exec.directReportsCount = exec.children!.length;
    ceo.children!.push(exec);
  });

  ceo.directReportsCount = ceo.children!.length;
  return ceo;
}

// ---------------------------------------------------------------------------
// Real-data scoping
//
// When the dashboard threads the viewed manager's real reportee sub-tree in,
// we build a HierarchyNode tree rooted at that manager from a flat member list
// (each member points at its manager via `reportsTo`, an id). This replaces the
// synthetic mock org so the drawer shows only the selected manager's sub-tree.
// The mock generator remains the fallback for the admin-builder preview, where
// no real manager context exists.
// ---------------------------------------------------------------------------

// A flat member row from the dashboard (myTeamAllMembers) plus the root manager
// (myTeamManagerProfile). Only the fields the tree needs are required.
export interface FlatMember {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
  department?: string;
  designation?: string;
  isManager?: boolean;
  reportsTo?: string;
}

export interface HierarchyRootMember {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
  department?: string;
  designation?: string;
}

// Assemble a HierarchyNode tree rooted at `root` from a flat member list keyed
// off `reportsTo`. Members are linked to their parent by id; any member whose
// `reportsTo` is missing/unknown is attached directly under the root so nothing
// is silently dropped. Guards against cycles via a visited set.
function buildRealHierarchy(
  root: HierarchyRootMember,
  members: FlatMember[],
): HierarchyNode {
  const toNode = (
    id: string,
    name: string,
    email: string,
    avatar: string,
    department: string,
    designation: string,
    isManager: boolean,
  ): HierarchyNode => ({
    id,
    name,
    email,
    avatar,
    department,
    designation,
    isManager,
    directReportsCount: 0,
    children: [],
  });

  const rootNode = toNode(
    root.id,
    root.name,
    root.email ?? '',
    root.avatar ?? '',
    root.department ?? '',
    root.designation ?? '',
    true,
  );

  // Build node map for every member (dedupe by id; skip the root if it also
  // appears in the flat list).
  const nodeById = new Map<string, HierarchyNode>();
  nodeById.set(rootNode.id, rootNode);
  for (const m of members) {
    if (!m.id || m.id === rootNode.id || nodeById.has(m.id)) continue;
    nodeById.set(
      m.id,
      toNode(
        m.id,
        m.name,
        m.email ?? '',
        m.avatar ?? '',
        m.department ?? '',
        m.designation ?? '',
        !!m.isManager,
      ),
    );
  }

  // Link each member under its manager (by reportsTo id), falling back to the
  // root when the parent is unknown/outside this sub-tree.
  for (const m of members) {
    if (!m.id || m.id === rootNode.id) continue;
    const node = nodeById.get(m.id);
    if (!node) continue;
    const parent =
      (m.reportsTo && nodeById.get(m.reportsTo)) || rootNode;
    parent.children!.push(node);
  }

  // Compute directReportsCount for every node.
  const setCounts = (node: HierarchyNode, seen: Set<string>) => {
    if (seen.has(node.id)) {
      node.children = [];
      node.directReportsCount = 0;
      return;
    }
    seen.add(node.id);
    node.directReportsCount = node.children?.length ?? 0;
    node.children?.forEach((c) => setCounts(c, seen));
  };
  setCounts(rootNode, new Set<string>());

  return rootNode;
}

// Count total nodes in hierarchy
function countNodes(node: HierarchyNode): number {
  let count = 1;
  if (node.children) {
    node.children.forEach(child => {
      count += countNodes(child);
    });
  }
  return count;
}

// Count managers in hierarchy
function countManagers(node: HierarchyNode): number {
  let count = node.isManager ? 1 : 0;
  if (node.children) {
    node.children.forEach(child => {
      count += countManagers(child);
    });
  }
  return count;
}

// Count direct reports (children of root)
function countDirectReports(node: HierarchyNode): number {
  return node.children?.length || 0;
}

// Count indirect reports (all descendants except direct children)
function countIndirectReports(node: HierarchyNode): number {
  const total = countNodes(node) - 1; // Exclude self
  const direct = countDirectReports(node);
  return total - direct;
}

// Extract unique departments from hierarchy — skips W5 sentinel '' so Radix
// <Select.Item> never receives an empty string value.
function extractDepartments(node: HierarchyNode, deps: Set<string> = new Set()): Set<string> {
  if (node.department) deps.add(node.department);
  node.children?.forEach(child => extractDepartments(child, deps));
  return deps;
}

// Extract unique designations from hierarchy — skips W5 sentinel ''.
function extractDesignations(node: HierarchyNode, desigs: Set<string> = new Set()): Set<string> {
  if (node.designation) desigs.add(node.designation);
  node.children?.forEach(child => extractDesignations(child, desigs));
  return desigs;
}

// Filter and search hierarchy
function filterHierarchy(
  node: HierarchyNode,
  searchQuery: string,
  department: string,
  designation: string,
  path: string[] = []
): { matchIds: Set<string>; expandIds: Set<string> } {
  const result = { matchIds: new Set<string>(), expandIds: new Set<string>() };
  const queryLower = searchQuery.toLowerCase();

  // Check if node matches all filters
  const matchesSearch = !searchQuery || 
    node.name.toLowerCase().includes(queryLower) ||
    node.email.toLowerCase().includes(queryLower) ||
    node.department.toLowerCase().includes(queryLower) ||
    node.designation.toLowerCase().includes(queryLower);

  const matchesDepartment = department === 'all' || node.department === department;
  const matchesDesignation = designation === 'all' || node.designation === designation;

  if (matchesSearch && matchesDepartment && matchesDesignation) {
    result.matchIds.add(node.id);
    // Add all ancestors to expand set
    path.forEach(id => result.expandIds.add(id));
  }

  if (node.children) {
    node.children.forEach(child => {
      const childResult = filterHierarchy(child, searchQuery, department, designation, [...path, node.id]);
      childResult.matchIds.forEach(id => result.matchIds.add(id));
      childResult.expandIds.forEach(id => result.expandIds.add(id));
    });
  }

  return result;
}

// Tree Node Component
interface TreeNodeProps {
  node: HierarchyNode;
  depth: number;
  expandedNodes: Set<string>;
  matchedNodes: Set<string>;
  focusedManagerId: string | null;
  siblingIds: string[];
  onToggle: (id: string) => void;
  onFocus: (id: string | null) => void;
  searchQuery: string;
  hasActiveFilters: boolean;
  scrollToNodeId: string | null;
  onScrollComplete: () => void;
}

function TreeNode({ 
  node, 
  depth, 
  expandedNodes, 
  matchedNodes, 
  focusedManagerId,
  siblingIds,
  onToggle, 
  onFocus,
  searchQuery,
  hasActiveFilters,
  scrollToNodeId,
  onScrollComplete
}: TreeNodeProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const isExpanded = expandedNodes.has(node.id);
  const isMatched = matchedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;

  // Scroll to this node if it's the target
  useEffect(() => {
    if (scrollToNodeId === node.id && nodeRef.current) {
      nodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onScrollComplete();
    }
  }, [scrollToNodeId, node.id, onScrollComplete]);

  // Determine if this node is greyed out (sibling is focused)
  const isSiblingFocused = focusedManagerId && 
    siblingIds.includes(focusedManagerId) && 
    focusedManagerId !== node.id;

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts.length >= 2 
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  };

  // Highlight matching text
  const highlightText = (text: string) => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark> : part
    );
  };

  // Handle expand/collapse with focus management
  const handleToggle = () => {
    if (hasChildren) {
      if (isExpanded) {
        // Collapsing - clear focus if this node was focused
        if (focusedManagerId === node.id) {
          onFocus(null);
        }
        onToggle(node.id);
      } else {
        // Expanding - set focus to this node
        onFocus(node.id);
        onToggle(node.id);
      }
    }
  };

  // Get initials background color based on name
  const getInitialsBgColor = (name: string) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-purple-100 text-purple-700',
      'bg-orange-100 text-orange-700',
      'bg-pink-100 text-pink-700',
      'bg-teal-100 text-teal-700',
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className={cn(
      "select-none transition-opacity duration-200",
      isSiblingFocused && "opacity-40"
    )}>
      {/* Node Row */}
      <div
        ref={nodeRef}
        className={cn(
          "flex items-center gap-2 py-2 px-2 rounded-lg cursor-pointer transition-all",
          "hover:bg-muted/50",
          isMatched && hasActiveFilters && "bg-yellow-50 border border-yellow-200",
          isExpanded && focusedManagerId === node.id && "bg-muted/30"
        )}
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
        onClick={handleToggle}
      >
        {/* Expand/Collapse Icon */}
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
          )}
        </div>

        {/* Avatar - Only for top 2 levels (depth 0, 1, 2) */}
        {depth <= 2 ? (
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={node.avatar} alt={node.name} />
            <AvatarFallback className="text-xs bg-primary/10 text-primary">
              {getInitials(node.name)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0",
            getInitialsBgColor(node.name)
          )}>
            {getInitials(node.name)}
          </div>
        )}

        {/* Name and Designation */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">
              {highlightText(node.name)}
            </span>
            {node.isManager && (
              <Users className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {highlightText(node.designation)}
          </div>
        </div>

        {/* Department Badge */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs flex-shrink-0 max-w-[100px] truncate">
                {node.department}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-xs">
                <p><strong>{node.name}</strong></p>
                <p>{node.email}</p>
                <p>{node.designation} • {node.department}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Team Size Badge */}
        {node.isManager && node.directReportsCount > 0 && (
          <Badge variant="secondary" className="text-xs flex-shrink-0">
            {node.directReportsCount}
          </Badge>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="relative">
          {/* Vertical connecting line */}
          <div 
            className="absolute left-0 top-0 bottom-0 w-px bg-border"
            style={{ marginLeft: `${depth * 24 + 18}px` }}
          />
          {node.children!.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              matchedNodes={matchedNodes}
              focusedManagerId={focusedManagerId}
              siblingIds={node.children!.map(c => c.id)}
              onToggle={onToggle}
              onFocus={onFocus}
              searchQuery={searchQuery}
              hasActiveFilters={hasActiveFilters}
              scrollToNodeId={scrollToNodeId}
              onScrollComplete={onScrollComplete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Main HierarchyTree Component
interface HierarchyTreeProps {
  /**
   * The viewed manager — root of the real reportee sub-tree. When provided
   * (together with `members`), the tree renders the manager's real sub-tree
   * instead of the synthetic mock org. Omit both to fall back to the mock
   * (admin-builder preview, where no real manager context exists).
   */
  rootMember?: HierarchyRootMember | null;
  /** Flat reportee list (each linked to its manager via `reportsTo` id). */
  members?: FlatMember[];
}

export function HierarchyTree({ rootMember, members }: HierarchyTreeProps = {}) {
  // Root the tree at the viewed manager's real sub-tree when data is threaded
  // in; otherwise synthesise the mock org (admin-builder preview only).
  const hierarchyData = useMemo(() => {
    if (rootMember && rootMember.id) {
      return buildRealHierarchy(rootMember, members ?? []);
    }
    return generateMockHierarchy();
  }, [rootMember, members]);
  
  // Stats
  const totalNodes = useMemo(() => countNodes(hierarchyData), [hierarchyData]);
  const totalManagers = useMemo(() => countManagers(hierarchyData), [hierarchyData]);
  const directReports = useMemo(() => countDirectReports(hierarchyData), [hierarchyData]);
  const indirectReports = useMemo(() => countIndirectReports(hierarchyData), [hierarchyData]);

  // Extract unique departments and designations
  const departments = useMemo(() => Array.from(extractDepartments(hierarchyData)).sort(), [hierarchyData]);
  const designations = useMemo(() => Array.from(extractDesignations(hierarchyData)).sort(), [hierarchyData]);

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [designationFilter, setDesignationFilter] = useState('all');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => new Set([hierarchyData.id]));
  const [focusedManagerId, setFocusedManagerId] = useState<string | null>(null);
  const [scrollToNodeId, setScrollToNodeId] = useState<string | null>(null);
  const prevFilterResultsRef = useRef<Set<string>>(new Set());

  // Check if any filters are active
  const hasActiveFilters = searchQuery.trim() !== '' || departmentFilter !== 'all' || designationFilter !== 'all';

  // Filter results
  const filterResults = useMemo(() => {
    if (!hasActiveFilters) {
      return { matchIds: new Set<string>(), expandIds: new Set<string>() };
    }
    return filterHierarchy(hierarchyData, searchQuery.trim(), departmentFilter, designationFilter);
  }, [hierarchyData, searchQuery, departmentFilter, designationFilter, hasActiveFilters]);

  // Combined expanded nodes (manual + filter-expanded)
  const effectiveExpandedNodes = useMemo(() => {
    const combined = new Set(expandedNodes);
    filterResults.expandIds.forEach(id => combined.add(id));
    return combined;
  }, [expandedNodes, filterResults.expandIds]);

  // Auto-scroll to first match when filter results change
  useEffect(() => {
    if (hasActiveFilters && filterResults.matchIds.size > 0) {
      const currentMatches = Array.from(filterResults.matchIds);
      const prevMatches = Array.from(prevFilterResultsRef.current);
      
      // Check if results actually changed
      if (currentMatches.length !== prevMatches.length || 
          currentMatches.some(id => !prevFilterResultsRef.current.has(id))) {
        // Scroll to first match after a brief delay for DOM to update
        setTimeout(() => {
          setScrollToNodeId(currentMatches[0]);
        }, 100);
      }
    }
    prevFilterResultsRef.current = filterResults.matchIds;
  }, [filterResults.matchIds, hasActiveFilters]);

  // Clear scroll target after scrolling
  const handleScrollComplete = useCallback(() => {
    setScrollToNodeId(null);
  }, []);

  // Toggle node expansion
  const handleToggle = (id: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Scroll to the expanded node after a brief delay
        setTimeout(() => {
          setScrollToNodeId(id);
        }, 50);
      }
      return next;
    });
  };

  // Expand all / Collapse all
  const expandAll = () => {
    const allIds = new Set<string>();
    const collectIds = (node: HierarchyNode) => {
      if (node.isManager) allIds.add(node.id);
      node.children?.forEach(collectIds);
    };
    collectIds(hierarchyData);
    setExpandedNodes(allIds);
    setFocusedManagerId(null);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set([hierarchyData.id]));
    setFocusedManagerId(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search and Filters */}
      <div className="p-4 border-b space-y-2">
        {/* Search + Filters on same row */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept} value={dept}>{dept}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={designationFilter} onValueChange={setDesignationFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Designation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Designations</SelectItem>
              {designations.map(desig => (
                <SelectItem key={desig} value={desig}>{desig}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Match count below */}
        {hasActiveFilters && (
          <p className="text-sm text-muted-foreground">
            Found <span className="font-medium text-foreground">{filterResults.matchIds.size}</span> matches
          </p>
        )}
      </div>

      {/* Summary Stats */}
      <div className="px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Total:</span>
              <span className="font-medium">{totalNodes.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Managers:</span>
              <span className="font-medium">{totalManagers}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <UserCheck className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Direct:</span>
              <span className="font-medium">{directReports}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <UsersRound className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Indirect:</span>
              <span className="font-medium">{indirectReports.toLocaleString()}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={expandAll}>
              Expand All
            </Button>
            <Button variant="outline" size="sm" onClick={collapseAll}>
              Collapse All
            </Button>
          </div>
        </div>
      </div>

      {/* Tree View */}
      <div className="flex-1 overflow-y-auto p-2">
        <TreeNode
          node={hierarchyData}
          depth={0}
          expandedNodes={effectiveExpandedNodes}
          matchedNodes={filterResults.matchIds}
          focusedManagerId={focusedManagerId}
          siblingIds={[]}
          onToggle={handleToggle}
          onFocus={setFocusedManagerId}
          searchQuery={searchQuery}
          hasActiveFilters={hasActiveFilters}
          scrollToNodeId={scrollToNodeId}
          onScrollComplete={handleScrollComplete}
        />
      </div>
    </div>
  );
}
